/**
 * Vibesite Event Catalog
 *
 * The single source of truth for the canonical event vocabulary that flows
 * through the runtime event bus. Every source block, every tracking sink, and
 * the editor's event-picker UI all reference this file.
 *
 * The catalog is a closed enum on purpose. Adding a new event is a code change
 * here, not a per-site UI affordance — that's what guarantees decoupling
 * between sources (which only know event names) and sinks (which only know
 * how to map names to their platform's API). Site authors who need free-form
 * event names should use GTM, which has its own dataLayer-push UI for that.
 *
 * Deliberately not in the catalog: `outbound_link_clicked`. Off-host is a
 * categorization, not a specific user action; auto-tagging would generate noise
 * from footer credits, social icons, and incidental links. Sites that want to
 * track a specific external destination add `data-vibesite-event="..."`
 * explicitly.
 */

// ============================================================================
// Event Categories (used to group entries in editor pickers)
// ============================================================================

export type VibesiteEventCategory = 'Forms' | 'Clicks' | 'CTAs';

// ============================================================================
// Standard Envelope
// ============================================================================

/**
 * Fields automatically attached to every event by the runtime, before the
 * source-specific payload is merged in. Sinks can rely on these always being
 * present.
 */
export interface VibesiteEventEnvelope {
  /** Page alias from the runtime page context (e.g. "home", "floorplans"). */
  page?: string;
  /** Page path from the runtime page context (e.g. "/floorplans"). */
  path?: string;
  /** Multihub property ID, when available. */
  propertyId?: string;
  /** Multihub company ID, when available. */
  companyId?: string;
  /** Block instance ID for events emitted from a block. */
  sectionId?: string;
  /** Block catalog ID for events emitted from a block. */
  blockId?: string;
  /** ISO timestamp set when the event is emitted. */
  emittedAt?: string;
}

// ============================================================================
// Per-Event Payload Types
// ============================================================================

export interface ContactFormSubmittedPayload extends VibesiteEventEnvelope {
  formId?: string;
  formFields?: Record<string, unknown>;
  leadId?: string;
}

export interface TourScheduledPayload extends VibesiteEventEnvelope {
  tourType?: string;
  appointmentDate?: string;
  appointmentTime?: string;
  formFields?: Record<string, unknown>;
}

export interface PhoneClickedPayload extends VibesiteEventEnvelope {
  phoneNumber?: string;
  href?: string;
  linkText?: string;
}

export interface EmailClickedPayload extends VibesiteEventEnvelope {
  emailAddress?: string;
  href?: string;
  linkText?: string;
}

export interface CtaClickedPayload extends VibesiteEventEnvelope {
  href?: string;
  target?: string;
  linkText?: string;
}

// ============================================================================
// Catalog
// ============================================================================

/**
 * The canonical event catalog. Adding a new event:
 *   1. Add a payload interface above (or reuse an existing one).
 *   2. Add an entry to VIBESITE_EVENTS with label, category, description.
 *   3. Add the payload type to VibesiteEventPayloadMap below.
 *   4. Optional: extend tracking-block EVENT_MAP entries to map it.
 */
export const VIBESITE_EVENTS = {
  // ── Forms (programmatic emit on success) ────────────────────────────────────
  contact_form_submitted: {
    label: 'Contact form submitted',
    category: 'Forms' as VibesiteEventCategory,
    description:
      'A lead form (Schedule a Tour, Contact Us, etc.) was submitted successfully.',
  },
  tour_scheduled: {
    label: 'Tour scheduled',
    category: 'Forms' as VibesiteEventCategory,
    description:
      'A tour was successfully scheduled through the tour scheduler block.',
  },

  // ── Clicks (auto-detected from href scheme) ─────────────────────────────────
  phone_clicked: {
    label: 'Phone number clicked',
    category: 'Clicks' as VibesiteEventCategory,
    description: 'User clicked a tel: link (auto-detected on any <a href="tel:...">).',
  },
  email_clicked: {
    label: 'Email link clicked',
    category: 'Clicks' as VibesiteEventCategory,
    description:
      'User clicked a mailto: link (auto-detected on any <a href="mailto:...">).',
  },

  // ── CTAs (annotated via data-vibesite-event or the slot-group event field) ──
  apply_now_clicked: {
    label: 'Apply Now clicked',
    category: 'CTAs' as VibesiteEventCategory,
    description: 'User clicked an Apply Online / Apply Now CTA.',
  },
  schedule_tour_clicked: {
    label: 'Schedule a Tour CTA clicked',
    category: 'CTAs' as VibesiteEventCategory,
    description:
      'User clicked a Schedule a Tour CTA (the click itself, not the form submission).',
  },
  self_guided_tour_clicked: {
    label: 'Self-guided tour CTA clicked',
    category: 'CTAs' as VibesiteEventCategory,
    description: 'User clicked a Self-Guided Tour CTA.',
  },
  virtual_tour_clicked: {
    label: 'Virtual tour CTA clicked',
    category: 'CTAs' as VibesiteEventCategory,
    description: 'User clicked a Virtual Tour CTA.',
  },
  resident_portal_clicked: {
    label: 'Resident portal CTA clicked',
    category: 'CTAs' as VibesiteEventCategory,
    description: 'User clicked a Resident Portal / Resident Login CTA.',
  },
} as const;

export type VibesiteEventName = keyof typeof VIBESITE_EVENTS;

/**
 * Nominal accessor for the canonical event names — derived from
 * `VIBESITE_EVENTS` so the catalog stays the single source of truth.
 *
 * Use this in source-block emits, sink-block EVENT_MAPs, and anywhere else
 * you'd otherwise hand-roll the string. You get autocomplete on `Event.`,
 * "Find Usages" works across the codebase, and renaming a catalog key is a
 * single edit (TypeScript flags every reference until you fix them).
 *
 * @example
 *   import { Event } from '@vibesites/runtime';
 *   bus.emit(Event.phone_clicked, { phoneNumber });
 *
 *   const GA4_EVENT_MAP = {
 *     [Event.phone_clicked]: { name: 'phone_call', params: (p) => ({...}) },
 *   };
 */
export const Event: { readonly [K in VibesiteEventName]: K } = Object.freeze(
  (Object.keys(VIBESITE_EVENTS) as VibesiteEventName[]).reduce<
    Record<string, string>
  >((acc, k) => {
    acc[k] = k;
    return acc;
  }, {}),
) as { readonly [K in VibesiteEventName]: K };

/**
 * Per-event payload type lookup. Used by the typed `emit()` overloads on the
 * bus so a sink that subscribes to a specific event gets a typed payload.
 */
export interface VibesiteEventPayloadMap {
  contact_form_submitted: ContactFormSubmittedPayload;
  tour_scheduled: TourScheduledPayload;
  phone_clicked: PhoneClickedPayload;
  email_clicked: EmailClickedPayload;
  apply_now_clicked: CtaClickedPayload;
  schedule_tour_clicked: CtaClickedPayload;
  self_guided_tour_clicked: CtaClickedPayload;
  virtual_tour_clicked: CtaClickedPayload;
  resident_portal_clicked: CtaClickedPayload;
}

/**
 * Any valid event payload — the union of all per-event payload types.
 */
export type VibesiteEventPayload = VibesiteEventPayloadMap[VibesiteEventName];

// ============================================================================
// Runtime Helpers
// ============================================================================

/**
 * Type guard — returns true when a string is a valid canonical event name.
 * Used by the DOM-capture layer and the (future) window.vibesites.events
 * global to validate inputs.
 */
export function isVibesiteEventName(name: unknown): name is VibesiteEventName {
  return (
    typeof name === 'string' &&
    Object.prototype.hasOwnProperty.call(VIBESITE_EVENTS, name)
  );
}

/**
 * Return the catalog as an array, useful for editor dropdowns.
 */
export function listVibesiteEvents(): Array<{
  name: VibesiteEventName;
  label: string;
  category: VibesiteEventCategory;
  description: string;
}> {
  return (Object.keys(VIBESITE_EVENTS) as VibesiteEventName[]).map((name) => ({
    name,
    ...VIBESITE_EVENTS[name],
  }));
}

/**
 * Group catalog entries by category, preserving definition order within each
 * group. Used by the editor's EventPicker.
 */
export function groupVibesiteEventsByCategory(): Array<{
  category: VibesiteEventCategory;
  events: Array<{ name: VibesiteEventName; label: string; description: string }>;
}> {
  const groups = new Map<
    VibesiteEventCategory,
    Array<{ name: VibesiteEventName; label: string; description: string }>
  >();
  for (const entry of listVibesiteEvents()) {
    const list = groups.get(entry.category) ?? [];
    list.push({
      name: entry.name,
      label: entry.label,
      description: entry.description,
    });
    groups.set(entry.category, list);
  }
  return Array.from(groups.entries()).map(([category, events]) => ({
    category,
    events,
  }));
}
